import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Loader2,
  Mail,
  Phone,
  GraduationCap,
  Award,
  FileText,
  X,
  Copy,
  Check,
  ChevronDown,
  TrendingUp,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from 'src/components/ui/sheet';
import { cn } from 'src/lib/utils';
import { useCandidateByContact } from 'src/services/api/queries/candidateQueries';
import { useLocationMap } from 'src/services/api/queries/referenceDataQueries';
import SkillPill from 'src/components/common/SkillPill';
import { sortWorkExperiences } from 'src/lib/workExperience';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const normalizeYear = (y) => {
  const n = parseInt(y, 10);
  if (!n) return n;
  if (n < 100) return n <= 50 ? 2000 + n : 1900 + n;
  return n;
};

const fmtMonthYear = (value) => {
  if (!value) return null;
  const str = String(value);
  if (str.includes('/')) {
    const parts = str.split('/');
    if (parts.length === 2) {
      // m/YYYY or m/YY
      const mi = parseInt(parts[0], 10);
      if (mi < 1 || mi > 12) return value;
      return `${MONTHS[mi - 1]} ${normalizeYear(parts[1])}`;
    }
    if (parts.length === 3) {
      // DD/MM/YYYY
      const mi = parseInt(parts[1], 10);
      if (mi < 1 || mi > 12) return value;
      return `${MONTHS[mi - 1]} ${parseInt(parts[2], 10)}`;
    }
  }
  if (str.includes('-')) {
    // YYYY-MM-DD (ISO format)
    const parts = str.split('-');
    const mi = parseInt(parts[1], 10);
    if (mi < 1 || mi > 12) return value;
    return `${MONTHS[mi - 1]} ${parseInt(parts[0], 10)}`;
  }
  return value;
};

const formatCTC = (value) => {
  if (!value) return null;
  const lakhs = value / 100000;
  return `₹${lakhs % 1 === 0 ? lakhs : lakhs.toFixed(1)}L`;
};

const titleCase = (s) =>
  s
    ? String(s)
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : s;

const nameFrom = (item) =>
  typeof item === 'string'
    ? item
    : item?.skill_name || item?.certification_name || item?.name || item?.label;

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'experience', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'education', label: 'Education' },
  { id: 'certifications', label: 'Certifications' },
];

const CandidatePreviewModal = ({
  open,
  onOpenChange,
  phone,
  email,
  fallbackName,
  resumeUrl,
  onViewProfile,
}) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [expandedExp, setExpandedExp] = useState(new Set([0]));
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [showAllStated, setShowAllStated] = useState(false);
  const [showAllEvidenced, setShowAllEvidenced] = useState(false);

  const locationMap = useLocationMap();

  const bodyRef = useRef(null);
  const sectionRefs = useRef({});

  const { data, isLoading, isError } = useCandidateByContact({
    phone,
    email,
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setActiveSection('overview');
      setExpandedExp(new Set([0]));
      setCopiedEmail(false);
      setCopiedPhone(false);
      setShowAllStated(false);
      setShowAllEvidenced(false);
    }
  }, [open]);

  const profile = data?.data?.profile_data;
  const personal = profile?.personal_information ?? {};
  const other = profile?.other_details ?? {};
  const education = profile?.educational_details ?? [];
  const work = sortWorkExperiences(profile?.work_experiences);
  const rawSkills = profile?.skills ?? [];
  const certifications = profile?.certifications ?? [];
  const candidateId = data?.data?.candidate_id;
  const registrationId = data?.data?.registration_id;

  const name =
    [personal.first_name, personal.middle_name, personal.last_name]
      .filter(Boolean)
      .join(' ') ||
    fallbackName ||
    'Candidate';

  const initials =
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || 'C';

  const candidateCode =
    registrationId || candidateId
      ? registrationId ||
        String(candidateId).replace(/-/g, '').slice(0, 8).toUpperCase()
      : null;

  const totalExpMonthsRaw = Number(other.total_work_experience);
  let totalExpDisplay = null;
  if (Number.isFinite(totalExpMonthsRaw) && totalExpMonthsRaw > 0) {
    const yrs = Math.floor(totalExpMonthsRaw / 12);
    const mos = totalExpMonthsRaw % 12;
    if (yrs > 0 && mos > 0) totalExpDisplay = `${yrs}y ${mos}m`;
    else if (yrs > 0) totalExpDisplay = `${yrs}y`;
    else totalExpDisplay = `${mos}m`;
  }

  const currentRole = work.find((w) => w.is_current) || work[0] || null;
  const currentCompany = currentRole?.company_name || null;
  const currentRoleTitle = currentRole?.role || null;

  const currentTenure = (() => {
    if (!currentRole?.from_date) return null;
    const dateStr = String(currentRole.from_date);
    let monthNum;
    let fullYear;
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 2) {
        // m/YYYY or m/YY
        monthNum = parseInt(parts[0], 10);
        fullYear = normalizeYear(parts[1]);
      } else if (parts.length === 3) {
        // DD/MM/YYYY
        monthNum = parseInt(parts[1], 10);
        fullYear = parseInt(parts[2], 10);
      } else {
        return null;
      }
    } else if (dateStr.includes('-')) {
      // YYYY-MM-DD (ISO format)
      const parts = dateStr.split('-');
      fullYear = parseInt(parts[0], 10);
      monthNum = parseInt(parts[1], 10);
    } else {
      return null;
    }
    if (!monthNum || !fullYear || monthNum < 1 || monthNum > 12) return null;
    const start = new Date(0);
    start.setFullYear(fullYear, monthNum - 1, 1);
    const now = new Date();
    if (start > now) return null;
    const months =
      (now.getFullYear() - start.getFullYear()) * 12 +
      (now.getMonth() - start.getMonth());
    if (months < 0 || months > 720) return null;
    if (months < 12) return `${months}m`;
    const yrs = Math.floor(months / 12);
    const rem = months % 12;
    return rem > 0 ? `${yrs}y ${rem}m` : `${yrs}y`;
  })();

  const orgCount = work.length;

  const statedSkills = rawSkills
    .map((s) => ({
      name: nameFrom(s) || '',
      proficiency: s.proficiency_level || s.proficiency || null,
    }))
    .filter((s) => s.name);

  const statedSkillNames = new Set(
    statedSkills.map((s) => s.name.toLowerCase())
  );

  const evidencedSkills = [];
  const evidencedSet = new Set();
  work.forEach((exp) => {
    (exp.duties ?? []).forEach((duty) => {
      (duty.skill_mentions ?? []).forEach((sm) => {
        const sname =
          typeof sm === 'string' ? sm : sm?.name || sm?.skill_name || '';
        if (
          sname &&
          !statedSkillNames.has(sname.toLowerCase()) &&
          !evidencedSet.has(sname.toLowerCase())
        ) {
          evidencedSkills.push(sname);
          evidencedSet.add(sname.toLowerCase());
        }
      });
    });
  });

  const domainFocus = (() => {
    const seen = new Set();
    const domains = [];
    work.forEach((exp) => {
      const d = exp.domain || exp.industry || exp.sector;
      if (d && !seen.has(String(d).toLowerCase())) {
        domains.push(d);
        seen.add(String(d).toLowerCase());
      }
    });
    return domains;
  })();

  const missingPrefs = [
    ...(!personal.notice_period ? ['notice_period'] : []),
    ...(!personal.expected_ctc ? ['expected_ctc'] : []),
    ...(!personal.preferred_work_locations?.length
      ? ['preferred_locations']
      : []),
  ];

  const displayEmail = personal.email || email;
  const displayPhone = personal.phone || phone;

  const isCurrentlyEmployed = !!work.find((exp) => exp.is_current);
  const isLeadership = !!(other.is_leadership || personal.is_leadership);

  const scrollToSection = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (el && bodyRef.current) {
      bodyRef.current.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' });
    }
    setActiveSection(id);
  }, []);

  const handleCopyEmail = useCallback(() => {
    if (!displayEmail) return;
    navigator.clipboard.writeText(displayEmail).then(() => {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 1800);
    });
  }, [displayEmail]);

  const handleCopyPhone = useCallback(() => {
    if (!displayPhone) return;
    navigator.clipboard.writeText(displayPhone).then(() => {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 1800);
    });
  }, [displayPhone]);

  useEffect(() => {
    let cleanup;
    if (profile && bodyRef.current) {
      const body = bodyRef.current;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(entry.target.dataset.section);
            }
          });
        },
        { root: body, rootMargin: '-20% 0px -60% 0px', threshold: 0 }
      );
      Object.values(sectionRefs.current).forEach((el) => {
        if (el) observer.observe(el);
      });
      cleanup = () => observer.disconnect();
    }
    return cleanup;
  }, [profile]);

  const toggleExp = (idx) => {
    setExpandedExp((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[540px] !max-w-[540px] p-0 flex flex-col gap-0 bg-card overflow-hidden"
        closeClassName="hidden"
      >
        <SheetTitle className="sr-only">Candidate preview</SheetTitle>

        {/* Header */}
        <div className="relative px-6 pt-5 pb-4 border-b border-borderColorSecondary shrink-0">
          <SheetClose className="absolute top-4 right-4 w-[28px] h-[28px] rounded-[7px] bg-muted flex items-center justify-center text-muted-foreground hover:bg-border hover:text-foreground transition-colors focus:outline-none">
            <X className="w-[13px] h-[13px]" strokeWidth={2.2} />
            <span className="sr-only">Close</span>
          </SheetClose>

          {candidateCode && (
            <div className="mb-3">
              <span className="text-[10.5px] font-semibold tracking-[0.07em] uppercase text-foreground-muted bg-muted px-[8px] py-[3px] rounded-[5px]">
                {candidateCode}
              </span>
            </div>
          )}

          <div className="flex items-start gap-4">
            <div
              className="w-[62px] h-[62px] min-w-[62px] rounded-full flex items-center justify-center text-[20px] font-bold tracking-[0.02em] shrink-0"
              style={{
                background:
                  'linear-gradient(135deg, var(--ds-success-subtle, #E8F8F1), #BBEBD7)',
                color: 'var(--ds-success-deep, #0D6E4A)',
              }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0 pr-8">
              <div className="text-[17px] font-bold tracking-[-0.02em] text-foreground-strong leading-[1.25]">
                {name}
              </div>
              {(currentRoleTitle || currentCompany) && (
                <div className="text-[13px] text-muted-foreground mt-[3px] leading-[1.4]">
                  {[currentRoleTitle, currentCompany]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
              <div className="flex flex-wrap gap-[6px] mt-[8px]">
                {isCurrentlyEmployed && (
                  <span className="text-[10.5px] font-[500] px-[8px] py-[3px] rounded-[5px] bg-ds-success-subtle text-ds-success-deep border border-ds-success-tint">
                    Currently employed
                  </span>
                )}
                {isLeadership && (
                  <span className="text-[10.5px] font-[500] px-[8px] py-[3px] rounded-[5px] bg-brand-bg-subtle text-primary border border-[#E1ECF7]">
                    Leadership
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact buttons */}
          <div className="flex items-center gap-[8px] mt-[14px] flex-wrap">
            {displayEmail && (
              <button
                type="button"
                onClick={handleCopyEmail}
                className={cn(
                  'group inline-flex items-center gap-[6px] text-[12.5px] font-[500] px-[10px] py-[6px] rounded-[7px] border transition-colors',
                  copiedEmail
                    ? 'border-ds-success text-ds-success-deep bg-ds-success-subtle'
                    : 'border-borderColorSecondary bg-card text-muted-foreground hover:border-primary hover:text-primary'
                )}
              >
                {copiedEmail ? (
                  <>
                    <Check className="w-[12px] h-[12px]" strokeWidth={2} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Mail
                      className="w-[12px] h-[12px] group-hover:hidden"
                      strokeWidth={1.8}
                    />
                    <Copy
                      className="w-[12px] h-[12px] hidden group-hover:inline-block"
                      strokeWidth={1.8}
                    />
                    <span className="group-hover:hidden">Email</span>
                    <span className="hidden group-hover:inline">
                      Copy email
                    </span>
                  </>
                )}
              </button>
            )}
            {displayPhone && (
              <button
                type="button"
                onClick={handleCopyPhone}
                className={cn(
                  'group inline-flex items-center gap-[6px] text-[12.5px] font-[500] px-[10px] py-[6px] rounded-[7px] border transition-colors',
                  copiedPhone
                    ? 'border-ds-success text-ds-success-deep bg-ds-success-subtle'
                    : 'border-borderColorSecondary bg-card text-muted-foreground hover:border-primary hover:text-primary'
                )}
              >
                {copiedPhone ? (
                  <>
                    <Check className="w-[12px] h-[12px]" strokeWidth={2} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Phone
                      className="w-[12px] h-[12px] group-hover:hidden"
                      strokeWidth={1.8}
                    />
                    <Copy
                      className="w-[12px] h-[12px] hidden group-hover:inline-block"
                      strokeWidth={1.8}
                    />
                    <span className="group-hover:hidden">Phone</span>
                    <span className="hidden group-hover:inline">
                      Copy phone
                    </span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* 3-col stats grid */}
          {!isLoading && !isError && profile && (
            <div className="grid grid-cols-3 border border-borderColorSecondary rounded-[10px] overflow-hidden mt-[14px]">
              <div className="flex flex-col items-center py-[10px] px-3 bg-card border-r border-borderColorSecondary">
                <span className="text-[18px] font-bold tracking-[-0.02em] text-foreground-strong tabular-nums">
                  {totalExpDisplay ?? '—'}
                </span>
                <span className="text-[10.5px] text-foreground-muted mt-[2px]">
                  Experience
                </span>
              </div>
              <div className="flex flex-col items-center py-[10px] px-3 bg-card border-r border-borderColorSecondary">
                <span className="text-[18px] font-bold tracking-[-0.02em] text-foreground-strong tabular-nums">
                  {orgCount}
                </span>
                <span className="text-[10.5px] text-foreground-muted mt-[2px]">
                  Organizations
                </span>
              </div>
              <div className="flex flex-col items-center py-[10px] px-3 bg-card">
                <span className="text-[18px] font-bold tracking-[-0.02em] text-foreground-strong tabular-nums">
                  {currentTenure || '—'}
                </span>
                <span className="text-[10.5px] text-foreground-muted mt-[2px]">
                  In current role
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex-1 flex items-center justify-center gap-2 text-[13px] text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading candidate…
          </div>
        )}

        {/* Error / not found */}
        {!isLoading && (isError || !profile) && (
          <div className="flex-1 flex items-center justify-center px-8 text-center">
            <p className="text-[13px] text-muted-foreground">
              {isError
                ? 'Could not load candidate details.'
                : 'No candidate record found for this contact.'}
            </p>
          </div>
        )}

        {/* Profile content */}
        {!isLoading && !isError && profile && (
          <>
            {/* Section nav */}
            <div className="flex items-center border-b border-borderColorSecondary bg-card shrink-0 overflow-x-auto scrollbar-none">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToSection(item.id)}
                  className={cn(
                    'px-[14px] py-[11px] text-[12.5px] font-[500] whitespace-nowrap transition-colors border-b-[2px] -mb-px',
                    activeSection === item.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Scrollable body */}
            <div
              ref={bodyRef}
              className="flex-1 overflow-y-auto scrollbar-thin"
            >
              {/* Overview */}
              <div
                ref={(el) => {
                  sectionRefs.current.overview = el;
                }}
                data-section="overview"
                className="px-6 py-5 border-b border-borderColorSecondary"
              >
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-foreground-muted mb-3">
                  Snapshot
                </div>

                <div className="grid grid-cols-2 gap-px bg-borderColorSecondary border border-borderColorSecondary rounded-[10px] overflow-hidden">
                  {[
                    { label: 'Phone', value: displayPhone },
                    { label: 'Email', value: displayEmail },
                    {
                      label: 'Current CTC',
                      value: formatCTC(personal.current_ctc),
                    },
                    {
                      label: 'Expected CTC',
                      value: formatCTC(personal.expected_ctc),
                    },
                    {
                      label: 'Notice Period',
                      value: personal.notice_period
                        ? `${personal.notice_period} days`
                        : null,
                    },
                    {
                      label: 'Preferred Locations',
                      value:
                        Array.isArray(personal.preferred_work_locations) &&
                        personal.preferred_work_locations.length > 0
                          ? personal.preferred_work_locations
                              .map((loc) => {
                                if (loc && typeof loc === 'object') {
                                  const locId = loc.id ?? loc.city_id;
                                  return (
                                    loc.name ||
                                    loc.city_name ||
                                    locationMap[locId] ||
                                    locId
                                  );
                                }
                                return locationMap[loc] || loc;
                              })
                              .filter(Boolean)
                              .join(', ')
                          : null,
                    },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-card px-[14px] py-[11px]">
                      <div className="text-[11px] font-[500] text-foreground-muted mb-[3px]">
                        {label}
                      </div>
                      <div className="text-[13px] font-[500] text-foreground-strong truncate">
                        {value || (
                          <span className="text-[12px] text-foreground-muted font-normal italic">
                            Not on file
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {missingPrefs.length > 0 && (
                  <div className="mt-3 flex items-start gap-[8px] px-[12px] py-[10px] rounded-[8px] bg-ds-warning-subtle border border-ds-warning-tint">
                    <AlertTriangle
                      className="w-[13px] h-[13px] text-ds-warning shrink-0 mt-[1px]"
                      strokeWidth={1.8}
                    />
                    <span className="text-[11.5px] text-ds-warning leading-[1.45]">
                      Missing:{' '}
                      {missingPrefs
                        .map((k) => titleCase(k.replace(/_/g, ' ')))
                        .join(', ')}
                    </span>
                  </div>
                )}

                {domainFocus.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-foreground-muted mb-[8px]">
                      Domain focus
                    </div>
                    <div className="flex flex-wrap gap-[6px]">
                      {domainFocus.map((d) => (
                        <span
                          key={d}
                          className="text-[11.5px] font-[500] px-[9px] py-[4px] rounded-[6px] bg-brand-bg-subtle text-primary border border-[#E1ECF7]"
                        >
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Experience */}
              <div
                ref={(el) => {
                  sectionRefs.current.experience = el;
                }}
                data-section="experience"
                className="px-6 py-5 border-b border-borderColorSecondary"
              >
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-foreground-muted mb-4">
                  Experience
                  {work.length > 0 && (
                    <span className="ml-[5px]">({work.length})</span>
                  )}
                </div>

                {work.length ? (
                  <div className="relative pl-[22px]">
                    <div className="absolute left-[7px] top-[8px] bottom-[8px] w-px bg-borderColorSecondary" />
                    {work.map((exp, idx) => {
                      const isExpanded = expandedExp.has(idx);
                      const isCurrent = !!exp.is_current;
                      const from = fmtMonthYear(exp.from_date);
                      // "Present" only when the backend marks it current — a
                      // missing to_date is a capture gap, not an ongoing role.
                      const to = isCurrent
                        ? 'Present'
                        : fmtMonthYear(exp.to_date);

                      return (
                        <div
                          key={exp.id ?? idx}
                          className="relative mb-5 last:mb-0"
                        >
                          <div
                            className={cn(
                              'absolute left-[-22px] top-[6px] w-[14px] h-[14px] rounded-full border-2',
                              isCurrent
                                ? 'border-ds-success bg-ds-success shadow-[0_0_0_3px_var(--ds-success-subtle)]'
                                : 'border-borderColorSecondary bg-muted'
                            )}
                          />

                          <button
                            type="button"
                            onClick={() => toggleExp(idx)}
                            className="w-full text-left flex items-start justify-between gap-2"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-[13.5px] font-semibold text-foreground-strong leading-[1.35]">
                                {exp.role || '—'}
                              </div>
                              <div className="text-[12.5px] text-muted-foreground mt-[2px] flex items-center gap-[6px] flex-wrap">
                                <span>{exp.company_name || '—'}</span>
                                {isCurrent && (
                                  <span className="text-[10px] font-[500] px-[6px] py-[2px] rounded-[4px] bg-ds-success-subtle text-ds-success-deep">
                                    Current
                                  </span>
                                )}
                              </div>
                              {(from || to) && (
                                <div className="text-[11.5px] text-foreground-muted mt-[3px] tabular-nums">
                                  {from} – {to || '—'}
                                </div>
                              )}
                            </div>
                            <ChevronDown
                              className={cn(
                                'w-[14px] h-[14px] text-foreground-muted shrink-0 mt-[3px] transition-transform',
                                isExpanded && 'rotate-180'
                              )}
                              strokeWidth={2}
                            />
                          </button>

                          {isExpanded &&
                            exp.duties &&
                            exp.duties.length > 0 && (
                              <div className="mt-[10px] flex flex-col gap-[8px]">
                                {exp.duties.map((duty, di) => {
                                  const isAchievement =
                                    duty.type === 'achievement' ||
                                    duty.duty_type === 'achievement';
                                  const dutyText =
                                    duty.description ||
                                    duty.text ||
                                    duty.duty ||
                                    '';
                                  const mentions = duty.skill_mentions ?? [];
                                  const dutyKey =
                                    duty.id ??
                                    (dutyText.slice(0, 30) || `d${di}`);

                                  return (
                                    <div
                                      key={dutyKey}
                                      className="flex gap-[8px] items-start"
                                    >
                                      <span
                                        className={cn(
                                          'w-[18px] h-[18px] min-w-[18px] rounded-[5px] flex items-center justify-center shrink-0 mt-[1px]',
                                          isAchievement
                                            ? 'bg-ds-success-subtle'
                                            : 'bg-muted'
                                        )}
                                      >
                                        {isAchievement ? (
                                          <TrendingUp
                                            className="w-[10px] h-[10px] text-ds-success-deep"
                                            strokeWidth={2}
                                          />
                                        ) : (
                                          <Check
                                            className="w-[10px] h-[10px] text-muted-foreground"
                                            strokeWidth={2}
                                          />
                                        )}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-[12.5px] text-foreground-strong leading-[1.5]">
                                          {dutyText}
                                        </div>
                                        {mentions.length > 0 && (
                                          <div className="flex flex-wrap gap-[4px] mt-[5px]">
                                            {mentions.slice(0, 4).map((sm) => {
                                              const sname =
                                                typeof sm === 'string'
                                                  ? sm
                                                  : sm?.name ||
                                                    sm?.skill_name ||
                                                    '';
                                              return sname ? (
                                                <span
                                                  key={sname}
                                                  className="text-[10.5px] font-[500] px-[7px] py-[2px] rounded-[5px] bg-muted text-muted-foreground"
                                                >
                                                  {sname}
                                                </span>
                                              ) : null;
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[12.5px] italic text-foreground-muted m-0">
                    None added
                  </p>
                )}
              </div>

              {/* Skills */}
              <div
                ref={(el) => {
                  sectionRefs.current.skills = el;
                }}
                data-section="skills"
                className="px-6 py-5 border-b border-borderColorSecondary"
              >
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-foreground-muted mb-4">
                  Skills
                  {rawSkills.length > 0 && (
                    <span className="ml-[5px]">({rawSkills.length})</span>
                  )}
                </div>

                {statedSkills.length > 0 && (
                  <div className="mb-5">
                    <div className="text-[11.5px] text-foreground-muted font-[500] mb-[10px]">
                      Stated on resume
                    </div>
                    <div className="flex flex-wrap gap-[6px]">
                      {(showAllStated
                        ? statedSkills
                        : statedSkills.slice(0, 5)
                      ).map((sk) => (
                        <SkillPill key={sk.name} label={sk.name} />
                      ))}
                      {!showAllStated && statedSkills.length > 5 && (
                        <button
                          type="button"
                          onClick={() => setShowAllStated(true)}
                          className="text-[11.5px] font-[600] px-[9px] py-[4px] rounded-[6px] bg-muted text-muted-foreground hover:bg-border transition-colors"
                        >
                          +{statedSkills.length - 5} more
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {evidencedSkills.length > 0 && (
                  <div>
                    <div className="text-[11.5px] text-foreground-muted font-[500] mb-[8px]">
                      Evidenced in experience
                    </div>
                    <div className="flex flex-wrap gap-[6px]">
                      {(showAllEvidenced
                        ? evidencedSkills
                        : evidencedSkills.slice(0, 5)
                      ).map((sk) => (
                        <SkillPill key={sk} label={sk} />
                      ))}
                      {!showAllEvidenced && evidencedSkills.length > 5 && (
                        <button
                          type="button"
                          onClick={() => setShowAllEvidenced(true)}
                          className="text-[11.5px] font-[600] px-[9px] py-[4px] rounded-[6px] bg-muted text-muted-foreground hover:bg-border transition-colors"
                        >
                          +{evidencedSkills.length - 5} more
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {statedSkills.length === 0 && evidencedSkills.length === 0 && (
                  <p className="text-[12.5px] italic text-foreground-muted m-0">
                    None added
                  </p>
                )}
              </div>

              {/* Education */}
              <div
                ref={(el) => {
                  sectionRefs.current.education = el;
                }}
                data-section="education"
                className="px-6 py-5 border-b border-borderColorSecondary"
              >
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-foreground-muted mb-3">
                  Education
                  {education.length > 0 && (
                    <span className="ml-[5px]">({education.length})</span>
                  )}
                </div>

                {education.length ? (
                  <div className="flex flex-col gap-[8px]">
                    {education.map((edu) => (
                      <div
                        key={`${edu.id ?? edu.institution}-${edu.graduation_year}-${edu.qualification_type}`}
                        className="flex gap-3 px-[14px] py-[13px] border border-borderColorSecondary rounded-[10px] bg-card"
                      >
                        <span className="w-[32px] h-[32px] min-w-[32px] rounded-[8px] bg-brand-bg-subtle text-primary flex items-center justify-center shrink-0">
                          <GraduationCap
                            className="w-[15px] h-[15px]"
                            strokeWidth={1.8}
                          />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13.5px] font-semibold text-foreground-strong leading-[1.35]">
                            {titleCase(edu.qualification_type) || '—'}
                          </div>
                          <div className="text-[12.5px] text-muted-foreground mt-[2px]">
                            {edu.institution || '—'}
                          </div>
                        </div>
                        {edu.graduation_year && (
                          <div className="text-[12.5px] font-[500] text-foreground-muted shrink-0 tabular-nums self-center">
                            {edu.graduation_year}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[12.5px] italic text-foreground-muted m-0">
                    None added
                  </p>
                )}
              </div>

              {/* Certifications */}
              <div
                ref={(el) => {
                  sectionRefs.current.certifications = el;
                }}
                data-section="certifications"
                className="px-6 py-5"
              >
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-foreground-muted mb-3">
                  Certifications
                  {certifications.length > 0 && (
                    <span className="ml-[5px]">({certifications.length})</span>
                  )}
                </div>

                {certifications.length ? (
                  <div className="flex flex-col gap-[8px]">
                    {certifications.map((cert, ci) => {
                      const certName =
                        typeof cert === 'object'
                          ? cert?.certification_name ||
                            cert?.name ||
                            cert?.label
                          : String(cert);
                      const issuer =
                        typeof cert === 'object'
                          ? cert?.issuing_body || cert?.issuer
                          : null;
                      const category =
                        typeof cert === 'object'
                          ? cert?.category || cert?.cert_type
                          : null;
                      const certKey =
                        (typeof cert === 'object' ? cert?.id : null) ??
                        (certName || `cert-${ci}`);

                      return (
                        <div
                          key={certKey}
                          className="flex gap-3 px-[14px] py-[13px] border border-borderColorSecondary rounded-[10px] bg-card"
                        >
                          <span className="w-[32px] h-[32px] min-w-[32px] rounded-[8px] bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                            <Award
                              className="w-[15px] h-[15px]"
                              strokeWidth={1.8}
                            />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13.5px] font-semibold text-foreground-strong leading-[1.35]">
                              {certName || '—'}
                            </div>
                            {issuer && (
                              <div className="text-[12.5px] text-muted-foreground mt-[2px]">
                                {issuer}
                              </div>
                            )}
                            {category && (
                              <span className="mt-[5px] inline-block text-[10.5px] font-[500] px-[7px] py-[2px] rounded-[5px] bg-brand-bg-subtle text-primary">
                                {category}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[12.5px] italic text-foreground-muted m-0">
                    None added
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border p-[13px_24px] flex items-center gap-[9px] shrink-0">
              {onViewProfile && candidateId && (
                <button
                  type="button"
                  onClick={() => onViewProfile(candidateId)}
                  className={cn(
                    'flex-1 py-[9px] rounded-[8px] bg-primary text-primary-foreground',
                    'text-[13px] font-semibold hover:bg-primary/90 transition-colors',
                    'flex items-center justify-center gap-[6px]'
                  )}
                >
                  <ExternalLink className="w-[13px] h-[13px]" strokeWidth={2} />
                  View full profile
                </button>
              )}
              {resumeUrl && (
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[38px] h-[38px] rounded-[8px] border border-borderColorSecondary bg-card flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors shrink-0"
                >
                  <FileText className="w-[15px] h-[15px]" strokeWidth={1.8} />
                </a>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CandidatePreviewModal;
